import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { validatePasswordStrength } from '../utils/passwordUtils'
import { Eye, EyeOff, CheckCircle, XCircle, AlertCircle, ArrowRight } from 'lucide-react'
import { authService } from '../services'

const FirstTimePasswordResetPage = ({ dashboardType = 'admin' }) => {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get user data from location state
  const userData = location.state?.userData || {}
  const userRole = userData.role || ''
  
  // Redirect to login if no user data is provided
  useEffect(() => {
    if (!userData || Object.keys(userData).length === 0) {
      navigate('/login', { replace: true })
    }
  }, [userData, navigate])
  
  // Show loading if no user data yet
  if (!userData || Object.keys(userData).length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="text-sm sm:text-base text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }
  
  // Form state
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  
  // UI state
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [], isValid: false })
  const [isPasswordResetComplete, setIsPasswordResetComplete] = useState(false)
  
  // Password strength validation
  useEffect(() => {
    if (formData.newPassword) {
      const strength = validatePasswordStrength(formData.newPassword)
      setPasswordStrength(strength)
    } else {
      setPasswordStrength({ score: 0, feedback: [], isValid: false })
    }
  }, [formData.newPassword])
  
  // Form validation
  const validateForm = () => {
    const newErrors = {}
    
    // Validate new password
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required'
    } else if (!passwordStrength.isValid) {
      newErrors.newPassword = 'Password does not meet strength requirements'
    }
    
    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setIsLoading(true)
    setErrors({})
    
    try {
      // Call the API to reset password
      const result = await authService.firstTimePasswordReset({
        userId: userData.user_id,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      })
      
      if (result.success) {
        // Mark password reset as complete
        setIsPasswordResetComplete(true)
        
        const alreadyVerifiedOtp = location.state?.fromOtpVerified;
        setTimeout(() => {
          if (alreadyVerifiedOtp) {
            // Already verified OTP during login, redirect to login with clear success message
            navigate('/login', {
              state: {
                message: 'Password reset successful! Please login with your new password.',
                contact: location.state?.contact || userData.email,
                loginType: location.state?.loginType || 'email'
              }
            });
          } else {
            // Navigate to OTP verification with user data
            navigate('/otp', {
              state: {
                contact: location.state.contact || userData.email,
                userData: result.data.user,
                password: formData.newPassword, // Pass the new password
                isFirstLogin: true,
                fromPasswordReset: true,
                loginType: location.state.loginType || 'email'
              }
            });
          }
        }, 2500);
      } else {
        throw new Error(result.message || 'Failed to reset password')
      }
    } catch (error) {
      setErrors({ 
        general: error.message || 'Failed to reset password. Please try again.' 
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear field-specific errors
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }
  
  // Handle manual proceed button
  const handleProceed = () => {
    const alreadyVerifiedOtp = location.state?.fromOtpVerified;
    if (alreadyVerifiedOtp) {
      navigate('/login', {
        state: {
          message: 'Password reset successful! Please login with your new password.',
          contact: location.state?.contact || userData.email,
          loginType: location.state?.loginType || 'email'
        }
      });
    } else {
      navigate('/otp', {
        state: {
          userData: userData,
          contact: location.state?.contact || userData.email,
          password: formData.newPassword,
          isFirstLogin: true,
          fromPasswordReset: true,
          loginType: location.state?.loginType || 'email'
        }
      });
    }
  }

  // If password reset is complete, show success screen
  if (isPasswordResetComplete) {
    const alreadyVerifiedOtp = location.state?.fromOtpVerified;
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div>
              <CardTitle className="text-xl text-green-900">Password Reset Successful!</CardTitle>
              <CardDescription className="text-green-700 mt-2">
                Your password has been updated successfully.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {alreadyVerifiedOtp ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-emerald-900 mb-1">Account Ready</h4>
                    <p className="text-sm text-emerald-800">
                      Your permanent password has been configured. You can now login with your new credentials.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">Next Step: OTP Verification</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      To complete your first login, you need to verify your account with OTP.
                    </p>
                    <div className="text-xs text-blue-700 space-y-1">
                      <p>• We'll send an OTP to your registered email/phone</p>
                      <p>• For testing, you can use bypass code: <code className="bg-blue-100 px-1 rounded">123456</code></p>
                      <p>• After verification, you'll access your dashboard</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600">
                {alreadyVerifiedOtp 
                  ? 'Redirecting to login in 3 seconds...' 
                  : 'Redirecting to OTP verification in 3 seconds...'}
              </p>
              <Button 
                onClick={handleProceed}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <span>{alreadyVerifiedOtp ? 'Proceed to Login' : 'Proceed to OTP Verification'}</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
            </div>
          </div>
          <div>
            <CardTitle className="text-lg sm:text-xl text-blue-900">First Time Login</CardTitle>
            <CardDescription className="text-blue-700 mt-2">
              Welcome, {userData.name}! Please reset your temporary password to continue.
            </CardDescription>
          </div>
          </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
              {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{errors.general}</p>
                </div>
              )}
              
            {/* User Info Display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Account Information</h4>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Name:</strong> {userData.name}</p>
                <p><strong>Email:</strong> {userData.email}</p>
                <p><strong>Role:</strong> {userData.role?.replace('_', ' ')}</p>
              </div>
            </div>
            
            {/* New Password */}
            <div className="space-y-2">
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password *
                </label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                  className={`pr-10 ${errors.newPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              {errors.newPassword && (
                <p className="text-sm text-red-600">{errors.newPassword}</p>
              )}
                
                {/* Password Strength Indicator */}
                {formData.newPassword && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">Strength:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength.score <= 1 ? 'bg-red-500 w-1/4' :
                          passwordStrength.score === 2 ? 'bg-yellow-500 w-2/4' :
                          passwordStrength.score === 3 ? 'bg-blue-500 w-3/4' :
                          'bg-green-500 w-full'
                        }`}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score <= 1 ? 'text-red-600' :
                      passwordStrength.score === 2 ? 'text-yellow-600' :
                      passwordStrength.score === 3 ? 'text-blue-600' :
                      'text-green-600'
                    }`}>
                      {passwordStrength.score <= 1 ? 'Weak' :
                       passwordStrength.score === 2 ? 'Fair' :
                       passwordStrength.score === 3 ? 'Good' :
                       'Strong'}
                    </span>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div className="text-xs text-gray-600 space-y-1">
                      {passwordStrength.feedback.map((feedback, index) => (
                        <p key={index}>• {feedback}</p>
                    ))}
                  </div>
                  )}
                </div>
                )}
              </div>
              
            {/* Confirm Password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password *
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your new password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  className={`pr-10 ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500' : ''}`}
                  disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-600">{errors.confirmPassword}</p>
                    )}
                  </div>

            {/* Next Steps Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800">
                  <p className="font-medium mb-1">After password reset:</p>
                  <p>You'll be redirected to OTP verification to complete your first login.</p>
                </div>
              </div>
              </div>
              
              {/* Submit Button */}
              <Button
                type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-all duration-200"
              disabled={isLoading || !passwordStrength.isValid}
              >
                {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Resetting Password...</span>
                  </div>
                ) : (
                <div className="flex items-center justify-center space-x-2">
                  <span>Reset Password & Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
    </div>
  )
}

export default FirstTimePasswordResetPage
